import type { RouteRecordRaw } from "vue-router";
import FeatureFlags from "@/enum/FeatureFlags";

const Layout = () => import("@/layout/index.vue");

/**
 * The own personal mailboxes (mail module) under the profile. They sit next to the constant
 * `/profile` route as an async route, because only async routes are filtered by roles and
 * feature flags; the more specific path wins over `/profile`. The OAuth callback of the backend
 * returns here (`/profile/mailboxes/{id}?complete=<handle>`, `/profile/mailboxes?error=…`).
 */
const profileMailRoutes: RouteRecordRaw[] = [
  {
    path: "/profile/mailboxes",
    // Named so that it can also be removed by name (resetRouter uses the addRoute handles).
    name: "ProfileMailboxesRoot",
    component: Layout,
    meta: {
      requiresAuth: true,
      hidden: true,
      roles: ["user"],
      cff: [FeatureFlags.MAIL_APP],
    },
    children: [
      {
        path: "",
        name: "ProfileMailboxes",
        component: () => import("@/views/settings/email/connections/Connections.vue"),
        props: { scope: "personal" },
        meta: { title: "profile", hidden: true, noCache: true, activeMenu: "/profile" },
      },
      {
        path: ":id",
        name: "ProfileMailbox",
        component: () => import("@/views/settings/email/connections/ConnectionDetail.vue"),
        props: { scope: "personal" },
        meta: { title: "profile", hidden: true, noCache: true, activeMenu: "/profile" },
      },
    ],
  },
  {
    // The backend sends a callback whose state it cannot read (no connection known, so no idea
    // whether it was a shared one) to `/profile/mailboxes?error=OAUTH_STATE_INVALID`. Integrations
    // users normally hold `user` as well (the role UIs always keep it), but the API accepts e.g.
    // `["tech_admin"]` alone; such a user has no profile route above and would get the 404. They
    // land on the shared connections instead, which show the same error toast. Without `?error=`
    // the path stays what it was for them: not found.
    // A user with the profile route keeps it: equal paths match in registration order.
    path: "/profile/mailboxes",
    name: "ProfileMailboxesFallback",
    redirect: (to) =>
      to.query.error ? { path: "/settings/email/connections", query: to.query, hash: to.hash } : { path: "/404" },
    meta: {
      requiresAuth: true,
      hidden: true,
      roles: ["manager", "tech_admin"],
      cff: [FeatureFlags.MAIL_APP],
    },
  },
];

export default profileMailRoutes;
